/*
  STORE CUSTOMIZATION: holiday shipping deadlines, window.HeatDeadlines.

  Loaded with `defer` by snippets/custom-holiday-deadline.liquid, which also
  prints the config JSON ([data-heat-deadlines-config]: the shop metafield
  heat.holiday_deadlines as-is, plus the wording from Theme settings > Holiday
  shipping deadlines) and the server-rendered first paint of every surface.
  The data contract is documented in that snippet and in docs/CUSTOM-FILES.md.

  Pages are full-page cached, so this file re-decides the state against the
  real clock on load, every minute and on bfcache restore. It rewrites a
  surface only when its state key differs from the one the server printed
  (data-hd-key), so a fresh page never changes.

  Surfaces ([data-heat-deadline][data-hd-surface]) and when each starts:
    bar, bar-mobile  the announcement bar text swap (sections/static-announcement.liquid);
                     starts lead.bar days (theme setting hd_lead_days_bar,
                     default 7) before the first order_by
    cart             the line above Checkout in the slide cart; shown only while
                     assets/slide-cart.js sets data-hd-eligible="1"; starts
                     lead.lines days (hd_lead_days_lines, default 14) before
                     the first order_by
    gift             the deadline strip of sections/custom-gift-hero.liquid;
                     starts with the cart line
    table            sections/custom-holiday-deadlines.liquid on the shipping page;
                     ignores show_from, so it shows as soon as the data is valid
  No surface starts before show_from (the table excepted); a blank lead
  setting (null in the config) means it starts on show_from. There is no
  product page line (removed 2026-10-17).

  The snippet prints this file and its config only while the metafield is
  valid and the server date is on or before hide_after; the rest of the year
  nothing loads.

  Test hook (JS only, never changes cached HTML): add
  ?heat_deadlines_test=2026-12-15T09:59:00-08:00 to any URL while the script
  loads (from the day the metafield exists through hide_after). The clock is
  fixed at that time and the data is the live metafield;
  &heat_deadlines_sample=1 swaps in the built-in SAMPLE below.

  Plain ES5. Also loads in Node (module.exports) for the unit tests.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (root && !root.HeatDeadlines) {
    root.HeatDeadlines = api;
    api.boot();
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var DEFAULT_LABELS = {
    standard: 'standard shipping',
    priority: 'Priority shipping',
    express: 'UPS Express'
  };

  /* The 2026 dates Shipping wrote (2026-10-13); used only by the test hook
     when the live metafield is missing or invalid. */
  var SAMPLE = {
    v: 1,
    occasion: 'Christmas',
    show_from: '2026-11-01',
    hide_after: '2026-12-25',
    methods: [
      { id: 'standard', order_by: '2026-12-15T10:00:00-08:00' },
      { id: 'priority', order_by: '2026-12-17T14:00:00-08:00' },
      { id: 'express', order_by: '2026-12-22T14:00:00-08:00' }
    ]
  };

  var DEFAULT_COPY = {
    active: 'Order by [when] with [method] for [occasion] delivery in the US.',
    next: '[passed] has passed for [occasion]. In the US, order by [when] with [method].',
    whenDate: '[date], [time] PT',
    whenTomorrow: '[time] PT tomorrow',
    whenToday: '[time] PT today',
    last: 'Too late to ship by [occasion]? Give the <a href="/products/sauce-of-the-month">Hot Sauce of the Month Club</a> and print a <a href="/pages/hot-sauce-of-the-month-gift-notes">gift note</a> today, or send an <a href="/products/gift-card">e-gift card</a>.',
    barActive: '[occasion] delivery in the US: order by [when] with [method]',
    barNext: '[passed] has passed. In the US, order by [when] with [method]',
    barLast: 'Too late to ship by [occasion]? Give the Hot Sauce of the Month Club.',
    barMobileActive: 'Order by [when] for [occasion]',
    barMobileNext: 'Order [method] by [when_short]',
    barMobileLast: 'Last-minute gift: Hot Sauce of the Month Club',
    barLink: '/pages/cheap-quick-shipping#holiday-deadlines',
    barLastLink: '/products/sauce-of-the-month'
  };

  var ORDER_BY_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+-])(\d{2}):(\d{2})$/;
  var DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 864e5;

  function own(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }
  function trim(s) { return String(s).replace(/^\s+|\s+$/g, ''); }
  function isArray(a) { return Object.prototype.toString.call(a) === '[object Array]'; }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  /* Same five characters Liquid's escape filter rewrites. */
  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function dayNumber(y, m, d) { return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS); }

  function realDate(y, m, d) {
    var t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
  }

  /* "YYYY-MM-DD" to a day number, or null. */
  function parseDay(s) {
    if (typeof s !== 'string') return null;
    var a = DATE_RE.exec(s);
    if (!a) return null;
    var y = +a[1], m = +a[2], d = +a[3];
    if (!realDate(y, m, d)) return null;
    return dayNumber(y, m, d);
  }

  /* "YYYY-MM-DDTHH:MM:SS-08:00" to { ms, day, y, m, d, h, mi }, or null. The
     wall clock in the string is the Pacific time shown to shoppers. */
  function parseOrderBy(s) {
    if (typeof s !== 'string') return null;
    var a = ORDER_BY_RE.exec(s);
    if (!a) return null;
    var y = +a[1], m = +a[2], d = +a[3], h = +a[4], mi = +a[5], se = +a[6];
    var oh = +a[8], om = +a[9];
    if (!realDate(y, m, d) || h > 23 || mi > 59 || se > 59 || oh > 14 || om > 59) return null;
    var off = (oh * 60 + om) * (a[7] === '-' ? -1 : 1);
    return {
      ms: Date.UTC(y, m - 1, d, h, mi, se) - off * 6e4,
      day: dayNumber(y, m, d),
      y: y, m: m, d: d, h: h, mi: mi
    };
  }

  /* US Pacific offset in minutes at an instant: PDT (-420) from the second
     Sunday of March 2 AM PST to the first Sunday of November 2 AM PDT. */
  function nthSunday(y, month0, n) {
    var first = new Date(Date.UTC(y, month0, 1)).getUTCDay();
    return 1 + ((7 - first) % 7) + (n - 1) * 7;
  }
  function pacificOffset(ms) {
    var y = new Date(ms).getUTCFullYear();
    var start = Date.UTC(y, 2, nthSunday(y, 2, 2), 10);
    var end = Date.UTC(y, 10, nthSunday(y, 10, 1), 9);
    return ms >= start && ms < end ? -420 : -480;
  }
  function pacificDay(ms) { return Math.floor((ms + pacificOffset(ms) * 6e4) / DAY_MS); }

  function formatDate(t) {
    var wd = new Date(Date.UTC(t.y, t.m - 1, t.d)).getUTCDay();
    return DAYS[wd] + ', ' + MONTHS[t.m - 1] + ' ' + t.d;
  }
  function formatTime(t) {
    var h12 = t.h % 12 || 12;
    return h12 + (t.mi ? ':' + pad2(t.mi) : '') + ' ' + (t.h < 12 ? 'AM' : 'PM');
  }

  /* Validates the metafield value. Anything unusable returns null, which
     renders nothing. Unknown extra fields are ignored; a method is dropped
     when its order_by is malformed or when it has neither a label nor a known
     id. Methods come back sorted by order_by. */
  function parse(raw, labels) {
    labels = labels || DEFAULT_LABELS;
    var data = raw;
    if (typeof raw === 'string') {
      try { data = JSON.parse(raw); } catch (e) { return null; }
    }
    if (!data || typeof data !== 'object' || isArray(data) || data.v !== 1) return null;
    if (typeof data.occasion !== 'string' || !trim(data.occasion)) return null;
    var from = parseDay(data.show_from);
    var hide = parseDay(data.hide_after);
    if (from === null || hide === null || hide < from) return null;
    if (!isArray(data.methods)) return null;
    var methods = [];
    for (var i = 0; i < data.methods.length; i++) {
      var m = data.methods[i];
      if (!m || typeof m !== 'object') continue;
      var at = parseOrderBy(m.order_by);
      if (!at) continue;
      var label = typeof m.label === 'string' ? trim(m.label) : '';
      if (!label && typeof m.id === 'string' && own(labels, m.id)) label = trim(labels[m.id] || '');
      if (!label) continue;
      methods.push({ id: typeof m.id === 'string' ? m.id : '', label: label, at: at, index: i });
    }
    if (!methods.length) return null;
    methods.sort(function (a, b) { return a.at.ms - b.at.ms || a.index - b.index; });
    return { occasion: trim(data.occasion), from: from, hide: hide, methods: methods };
  }

  /* A surface's first day (a day number): the later of show_from and the
     Pacific day of the first order_by minus `lead` days. lead null, blank or
     not a number means show_from; negatives count as 0. The same rule as
     hd_from_eff in snippets/custom-holiday-deadline.liquid. */
  function startDay(data, lead) {
    if (!data) return null;
    if (typeof lead !== 'number' || !isFinite(lead)) return data.from;
    var n = Math.max(0, Math.floor(lead));
    return Math.max(data.from, data.methods[0].at.day - n);
  }

  /* off: before the start day, after hide_after, or no data. fromDay (a day
       number) replaces show_from as the start day when given: startDay()
       for the lead windows, 0 for the table (reference, shows as soon as the
       data is valid). Omitted, the start day is show_from.
     active: the first method whose order_by is still ahead; `passed` is the
       one just before it (null for the first), `when` is today / tomorrow /
       date, by Pacific calendar day.
     last: every order_by has passed, still on or before hide_after. */
  function evaluate(data, nowMs, fromDay) {
    if (!data) return { status: 'off', key: 'off' };
    var today = pacificDay(nowMs);
    var from = typeof fromDay === 'number' ? fromDay : data.from;
    if (today < from || today > data.hide) return { status: 'off', key: 'off' };
    for (var i = 0; i < data.methods.length; i++) {
      var m = data.methods[i];
      if (nowMs < m.at.ms) {
        var diff = m.at.day - today;
        var when = diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : 'date';
        return {
          status: 'active',
          index: i,
          method: m,
          passed: i > 0 ? data.methods[i - 1] : null,
          when: when,
          key: 'a:' + i + ':' + when
        };
      }
    }
    return { status: 'last', key: 'last' };
  }

  function copyOf(copy) {
    var out = {};
    for (var k in DEFAULT_COPY) {
      if (own(DEFAULT_COPY, k)) out[k] = copy && typeof copy[k] === 'string' && trim(copy[k]) ? copy[k] : DEFAULT_COPY[k];
    }
    return out;
  }

  function capFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  /* Fills a plain-text template (escaped first) or an HTML one (richtext,
     trusted theme setting) with escaped values. [when] and [when_short] are
     wrapped in <strong class="hd__when">. A token that opens the template is
     capitalised. */
  function fill(template, tokens, isHtml) {
    var lead = trim(template);
    var out = isHtml ? template : esc(template);
    for (var name in tokens) {
      if (!own(tokens, name)) continue;
      var value = tokens[name];
      if (lead.indexOf('[' + name + ']') === 0) value = capFirst(value);
      var html = esc(value);
      if (name === 'when' || name === 'when_short') html = '<strong class="hd__when">' + html + '</strong>';
      out = out.split('[' + name + ']').join(html);
    }
    return out;
  }

  /* [when]: "Tue, Dec 15, 10 AM PT" (whenDate), or the tomorrow / today
     wording. [when_short] is the same except the date form has no time. */
  function whenText(state, copy, short) {
    var t = state.method.at;
    if (state.when === 'today') return trim(copy.whenToday.split('[time]').join(formatTime(t)));
    if (state.when === 'tomorrow') return trim(copy.whenTomorrow.split('[time]').join(formatTime(t)));
    if (short) return formatDate(t);
    return trim(copy.whenDate.split('[date]').join(formatDate(t)).split('[time]').join(formatTime(t)));
  }

  /* HTML for one surface variant: 'full' (cart, gift strip),
     'bar' (desktop bar) or 'barMobile'. Empty string when off. */
  function message(state, data, copy, variant) {
    copy = copyOf(copy);
    variant = variant || 'full';
    if (!data || state.status === 'off') return '';
    var occasion = data.occasion;
    if (state.status === 'last') {
      if (variant === 'bar') return fill(copy.barLast, { occasion: occasion });
      if (variant === 'barMobile') return fill(copy.barMobileLast, { occasion: occasion });
      return fill(copy.last, { occasion: occasion }, true);
    }
    var template;
    if (variant === 'barMobile') template = state.passed ? copy.barMobileNext : copy.barMobileActive;
    else if (variant === 'bar') template = state.passed ? copy.barNext : copy.barActive;
    else template = state.passed ? copy.next : copy.active;
    var tokens = {
      occasion: occasion,
      method: state.method.label,
      passed: state.passed ? state.passed.label : '',
      time: formatTime(state.method.at),
      when_short: whenText(state, copy, true),
      when: whenText(state, copy, false)
    };
    return fill(template, tokens);
  }

  /* Rows for the shipping page table. */
  function tableRows(state, data, passedLabel) {
    if (!data || state.status === 'off') return '';
    var current = state.status === 'active' ? state.index : data.methods.length;
    var html = '';
    for (var i = 0; i < data.methods.length; i++) {
      var m = data.methods[i];
      var cls = i < current ? 'hd-table__row is-passed' : i === current ? 'hd-table__row is-next' : 'hd-table__row';
      html += '<tr class="' + cls + '"><td>' + esc(capFirst(m.label)) + '</td><td>' +
        '<span class="hd-table__when">' + esc(formatDate(m.at)) + ', ' + esc(formatTime(m.at)) + ' PT</span>' +
        (i < current ? ' <span class="hd-table__passed">' + esc(passedLabel || 'Passed') + '</span>' : '') +
        '</td></tr>';
    }
    return html;
  }

  /* ---------- DOM ---------- */

  var cache = { raw: null, config: null };

  function readConfig() {
    if (typeof document === 'undefined') return null;
    var el = document.querySelector('script[data-heat-deadlines-config]');
    if (!el) return null;
    if (cache.raw === el.textContent) return cache.config;
    var cfg = null;
    try { cfg = JSON.parse(el.textContent); } catch (e) { cfg = null; }
    cache.raw = el.textContent;
    cache.config = cfg;
    return cfg;
  }

  /* ?heat_deadlines_test=<ISO time> fixes the clock; returns null without it
     or when the value does not parse. Read once per page: Empire rewrites the
     URL to ?variant=... on a variant change, which drops the parameter. */
  var testCache;
  function testClock() {
    if (testCache === undefined) testCache = readTestClock();
    return testCache;
  }
  function readTestClock() {
    if (typeof location === 'undefined' || location.search.indexOf('heat_deadlines_test=') === -1) return null;
    var match = /[?&]heat_deadlines_test=([^&#]*)/.exec(location.search);
    if (!match) return null;
    var value;
    try { value = decodeURIComponent(match[1]); } catch (e) { return null; }
    value = value.replace(/ /g, '+');
    var t = parseOrderBy(value);
    var ms = t ? t.ms : (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?Z$/.test(value) ? Date.parse(value) : NaN);
    if (isNaN(ms)) return null;
    return { now: ms, sample: /[?&]heat_deadlines_sample=1(&|$)/.test(location.search) };
  }

  function setHidden(el, hidden) {
    if (hidden) {
      if (!el.hasAttribute('hidden')) el.setAttribute('hidden', '');
    } else if (el.hasAttribute('hidden')) {
      el.removeAttribute('hidden');
    }
  }

  function writeKey(el, key, html) {
    if (el.getAttribute('data-hd-key') === key) return;
    el.innerHTML = html;
    el.setAttribute('data-hd-key', key);
  }

  function applyBar(el, state, data, copy) {
    var surface = el.getAttribute('data-hd-surface');
    var bar = el.closest ? el.closest('[data-announcement-bar]') : null;
    var holder = el.parentNode;
    var normal = holder ? holder.querySelector('[data-hd-bar-normal]') : null;
    var on = state.status !== 'off';
    if (on) writeKey(el, state.key, message(state, data, copy, surface === 'bar-mobile' ? 'barMobile' : 'bar'));
    setHidden(el, !on);
    if (normal) setHidden(normal, on);
    if (surface !== 'bar' || !bar) return;
    var link = bar.querySelector('[data-hd-link]');
    if (!link) return;
    var c = copyOf(copy);
    var href = on ? (state.status === 'last' ? c.barLastLink : c.barLink) : link.getAttribute('data-hd-href-normal');
    if (href) {
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      var label = on ? trim(el.textContent || '') : link.getAttribute('data-hd-label-normal');
      if (label && link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
    }
    setHidden(link, !href);
  }

  function applyTable(el, state, data) {
    var on = state.status !== 'off';
    setHidden(el, !on);
    if (!on) return;
    var body = el.querySelector('[data-hd-rows]');
    if (body && body.getAttribute('data-hd-key') !== state.key) {
      body.innerHTML = tableRows(state, data, el.getAttribute('data-hd-passed-label'));
      body.setAttribute('data-hd-key', state.key);
    }
    var occ = el.querySelectorAll('[data-hd-occasion]');
    for (var i = 0; i < occ.length; i++) {
      if (occ[i].textContent !== data.occasion) occ[i].textContent = data.occasion;
    }
    var last = el.querySelector('[data-hd-last]');
    if (last) setHidden(last, state.status !== 'last');
  }

  var api = {
    SAMPLE: SAMPLE,
    DEFAULT_LABELS: DEFAULT_LABELS,
    DEFAULT_COPY: DEFAULT_COPY,
    parse: parse,
    startDay: startDay,
    evaluate: evaluate,
    message: message,
    tableRows: tableRows,
    pacificOffset: pacificOffset,
    pacificDay: pacificDay,
    parseOrderBy: parseOrderBy,
    formatDate: formatDate,
    formatTime: formatTime,
    state: { status: 'off', key: 'off' },
    data: null,

    /* Re-decides every surface. Cheap: a surface whose key has not changed
       is not touched. The slide cart calls this after each render. api.state
       and the return value are the plain show_from state; each surface uses
       its own start day. */
    refresh: function () {
      var cfg = readConfig();
      if (!cfg) return api.state;
      var labels = cfg.labels || DEFAULT_LABELS;
      /* Only a json-type metafield arrives as an object. A text metafield
         holding JSON arrives as a string, which Liquid cannot read either, so
         it renders nothing here too. */
      var raw = cfg.data && typeof cfg.data === 'object' ? cfg.data : null;
      var test = testClock();
      var data = null;
      if (test) {
        data = test.sample ? null : parse(raw, labels);
        if (!data) data = parse(SAMPLE, labels);
      } else {
        data = parse(raw, labels);
      }
      /* A device clock running behind must never reopen a deadline the
         server already closed: the clock never goes back past the moment the
         page was rendered. A clock running ahead still moves things on. */
      var rendered = typeof cfg.rendered === 'number' ? cfg.rendered * 1000 : 0;
      var now = test ? test.now : Math.max(Date.now(), rendered);
      var lead = cfg.lead && typeof cfg.lead === 'object' ? cfg.lead : {};
      var state = evaluate(data, now);
      var barState = evaluate(data, now, startDay(data, lead.bar));
      var linesState = evaluate(data, now, startDay(data, lead.lines));
      var tableState = evaluate(data, now, 0);
      api.state = state;
      api.data = data;
      var els = document.querySelectorAll('[data-heat-deadline]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var surface = el.getAttribute('data-hd-surface');
        if (surface === 'bar' || surface === 'bar-mobile') {
          applyBar(el, barState, data, cfg.copy);
        } else if (surface === 'table') {
          applyTable(el, tableState, data);
        } else if (surface === 'cart' || surface === 'gift') {
          var on = linesState.status !== 'off';
          if (on) writeKey(el, linesState.key, message(linesState, data, cfg.copy, 'full'));
          if (surface === 'cart') on = on && el.getAttribute('data-hd-eligible') === '1';
          setHidden(el, !on);
        }
      }
      return state;
    },

    boot: function () {
      function start() {
        api.refresh();
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }
      setInterval(api.refresh, 60000);
      document.addEventListener('shopify:section:load', start);
      window.addEventListener('pageshow', function (event) { if (event.persisted) api.refresh(); });
    }
  };

  return api;
});
